/**
 * Register Screen
 *
 * Captures basic profile details for a new BasicUser, then routes back into
 * the existing identifier/OTP flow so the user can verify and sign in.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuthRegisterMutation } from '../../../lib/graphql';
import { REGISTRATION_COLLISION_MSG } from 'lib/buyer-claim-gate';

interface RegisterScreenProps {
  onRegistered: (identifier: string) => Promise<void>;
  /**
   * What kind of account this register flow creates. Stored as a relationship
   * marker on the new user so downstream views can distinguish e.g. producers
   * from buyers without needing to look at vendor relationships.
   */
  userType?: 'producer' | 'buyer';
}

export function RegisterScreen({
  onRegistered,
  userType = 'producer',
}: RegisterScreenProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [acceptTos, setAcceptTos] = useState(false);
  const [canReceiveEmailUpdates, setCanReceiveEmailUpdates] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProducerCollision, setIsProducerCollision] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [registerUser] = useAuthRegisterMutation();

  const canSubmit = useMemo(
    () =>
      firstName.trim().length > 0 &&
      (email.trim() || phone.trim()) &&
      acceptTos,
    [firstName, email, phone, acceptTos],
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsProducerCollision(false);
    setSubmitting(true);

    try {
      const trimmedEmail = email.trim() || undefined;
      const trimmedPhone = phone.trim() || undefined;

      const { data } = await registerUser({
        variables: {
          firstName: firstName.trim(),
          lastName: lastName.trim() || undefined,
          email: trimmedEmail,
          phone: trimmedPhone,
          company: company.trim() || undefined,
          userType,
          acceptTos,
          acceptPrivacyPolicy: acceptTos,
          canReceiveEmailUpdates,
        },
      });

      if (!data?.authRegister?.success) {
        throw new Error('Failed to create account');
      }

      // Prefer email for OTP delivery if both are provided
      const identifier = trimmedEmail || trimmedPhone;
      if (!identifier) {
        throw new Error('An email or phone number is required');
      }

      await onRegistered(identifier);
    } catch (err: unknown) {
      // R4: ACCOUNT_IS_PRODUCER — the email belongs to a producer/processor/admin.
      // Show clear copy and a sign-in CTA instead of the generic error string.
      const errCode = (
        err as { graphQLErrors?: Array<{ extensions?: { code?: string } }> }
      )?.graphQLErrors?.[0]?.extensions?.code;
      if (errCode === 'ACCOUNT_IS_PRODUCER') {
        setIsProducerCollision(true);
        setError(REGISTRATION_COLLISION_MSG);
      } else {
        const message =
          err instanceof Error ? err.message : 'Failed to create account';
        setError(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Create your {userType === 'buyer' ? 'buyer' : 'producer'} account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We&apos;ll send a verification code to sign you in.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error">
          {error}
          {isProducerCollision && (
            <Box sx={{ mt: 1 }}>
              <Link href="/login" underline="always">
                Sign in to your producer account
              </Link>
            </Box>
          )}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="First name"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={submitting}
              autoFocus
              required
            />
            <TextField
              fullWidth
              label="Last name"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={submitting}
            />
          </Stack>

          <TextField
            fullWidth
            label="Email"
            type="email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            disabled={submitting}
            helperText="Email or phone is required."
          />

          <TextField
            fullWidth
            label="Phone number"
            variant="outlined"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1234567890"
            disabled={submitting}
          />

          <TextField
            fullWidth
            label="Company (optional)"
            variant="outlined"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            disabled={submitting}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={acceptTos}
                onChange={(e) => setAcceptTos(e.target.checked)}
                disabled={submitting}
              />
            }
            label={
              <Typography variant="body2">
                I accept the{' '}
                <Link
                  href="https://farmshare.co/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </Link>{' '}
                &{' '}
                <Link
                  href="https://farmshare.co/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Privacy Policy
                </Link>
              </Typography>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={canReceiveEmailUpdates}
                onChange={(e) => setCanReceiveEmailUpdates(e.target.checked)}
                disabled={submitting}
              />
            }
            label={
              <Typography variant="body2">
                I agree to receive job updates via email, or SMS if I provide a
                phone number
              </Typography>
            }
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting || !canSubmit}
          >
            {submitting ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </Button>
        </Stack>
      </form>
    </Stack>
  );
}
